// PRC-019 · B2: guía «Primeros pasos» con cinco pasos que se marcan solos.
import React from 'react';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Eyebrow, JourneyButton, JourneyCard, JourneyText } from '../components/JourneyBasics';
import { JourneyStep, Meter, StepList } from '../components/JourneyBlocks';
import OverviewEmptyState from '../components/OverviewEmptyState';
import { J } from '../journeyTokens';
import { formatLongDate } from '../journeyBilling';
import { downloadTemplate, requiredColumns, UPLOAD_FILES, usePricingOnboarding } from '../onboarding';
import { withReturnTo } from '../navigation';

// PRC-027: cada paso lleva a su pantalla con el origen, que muestra «Volver a Primeros pasos».
const GUIDE = '/primeros-pasos';

const FirstStepsPage: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading } = usePricingOnboarding();
  const steps = data?.steps;

  const defs: Array<{ key: keyof NonNullable<typeof steps>; title: string; description: string; target: string; actionLabel: string }> = [
    { key: 'account', title: 'Crear la cuenta', description: data?.account_created_at ? `Hecho el ${formatLongDate(data.account_created_at)}.` : 'Hecho.', target: '/profile', actionLabel: 'Ver mi perfil' },
    { key: 'sales', title: 'Cargar el histórico de ventas', description: 'CSV o Excel con tus ventas de los últimos 12 meses.', target: '/data-upload', actionLabel: 'Cargar archivo' },
    { key: 'quality', title: 'Revisar la calidad de los datos', description: 'Te señalamos filas incompletas, costes vacíos o precios extraños.', target: '/data-quality', actionLabel: 'Revisar calidad' },
    { key: 'overview', title: 'Ver dónde ganas y pierdes margen', description: 'Overview y Explorer de Margin Center.', target: '/margin-center/overview', actionLabel: 'Abrir Overview' },
    { key: 'optimization', title: 'Evaluar tu primer precio', description: 'Una combinación cliente-producto en Optimización.', target: '/optimization-center', actionLabel: 'Evaluar un precio' },
  ];
  const firstPending = defs.findIndex((d) => !steps?.[d.key]);
  const list: JourneyStep[] = defs.map((d, i) => {
    const done = !!steps?.[d.key];
    const current = !done && i === firstPending;
    return {
      title: d.title,
      description: d.description,
      state: done ? 'done' : current ? 'current' : 'pending',
      action: (
        <JourneyButton small kind={current ? 'primary' : 'ghost'} onClick={() => navigate(withReturnTo(d.target, GUIDE))}>
          {done ? 'Ver de nuevo' : d.actionLabel}
        </JourneyButton>
      ),
    };
  });
  const done = data?.done ?? 1;
  const total = data?.total ?? 5;

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 1180, fontFamily: J.font }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-end', gap: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxWidth: 640 }}>
          <Eyebrow>Guía de inicio</Eyebrow>
          <JourneyText variant="title">Tu cuenta, lista en cinco pasos</JourneyText>
          <JourneyText variant="lead">
            {done >= total
              ? 'Has completado los cinco pasos. Puedes volver aquí desde el menú de usuario.'
              : `Llevas ${done} de ${total}.${!steps?.sales ? ' El siguiente es el único imprescindible: sin ventas cargadas, las pantallas no tienen nada que enseñarte.' : ''}`}
          </JourneyText>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <JourneyText variant="small" component="span">{done} de {total}</JourneyText>
          <Box sx={{ width: 200 }}><Meter value={(done / total) * 100} /></Box>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'flex-start' }}>
        <Box sx={{ flex: '3 1 520px', minWidth: 0 }}>
          {isLoading ? <JourneyText variant="small">Cargando…</JourneyText> : <StepList steps={list} />}
        </Box>
        <JourneyCard sx={{ flex: '2 1 320px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <JourneyText variant="h3">Qué debe llevar el archivo</JourneyText>
          <JourneyText variant="small">La carga usa tres archivos. Estas son las columnas obligatorias de cada uno:</JourneyText>
          {UPLOAD_FILES.map((f) => (
            <Box key={f.type} sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <Box sx={{ fontSize: 14, fontWeight: 700, color: J.text }}>{f.label}</Box>
              <Box component="ul" sx={{ m: 0, pl: '18px', fontSize: 14, lineHeight: 1.6, color: J.body }}>
                {requiredColumns(f.type).map((c) => <li key={c.key}>{c.label} <Box component="span" sx={{ color: J.muted }}>({c.key})</Box></li>)}
              </Box>
              <Box component="button" type="button" onClick={() => downloadTemplate(f.type)} sx={{ all: 'unset', cursor: 'pointer', alignSelf: 'flex-start', color: J.skyText, fontWeight: 600, fontSize: 14 }}>
                Descargar plantilla de {f.label.toLowerCase()}
              </Box>
            </Box>
          ))}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: 12, color: J.muted, '&:before, &:after': { content: '""', flex: 1, height: '1px', bgcolor: J.border } }}>o</Box>
          <JourneyButton kind="ghost" small onClick={() => navigate(withReturnTo('/enviar-archivos', GUIDE))}>Enviar archivos al equipo para que los preparemos</JourneyButton>
        </JourneyCard>
      </Box>

      {!steps?.sales && !isLoading && (
        <Box component="section" sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <JourneyText variant="h3">Así se ve Overview antes de cargar datos</JourneyText>
          <OverviewEmptyState />
        </Box>
      )}
    </Box>
  );
};

export default FirstStepsPage;
